import { useState } from 'react';
import { useWorkflowKanban, useCreateWorkflow, useTransitionWorkflow } from '../api/intelligence';
import type { Workflow } from '../api/intelligence';

const STATUS_ORDER = ['draft', 'collecting', 'analyzing', 'reviewing', 'reporting', 'closed'];
const STATUS_LABELS: Record<string, string> = {
  draft: 'Brouillon',
  collecting: 'Collecte',
  analyzing: 'Analyse',
  reviewing: 'Revue',
  reporting: 'Rapport',
  closed: 'Clos',
};
const STATUS_COLORS: Record<string, string> = {
  draft: 'border-gray-500',
  collecting: 'border-blue-500',
  analyzing: 'border-amber-500',
  reviewing: 'border-purple-500',
  reporting: 'border-cyan-500',
  closed: 'border-green-500',
};
const PRIORITY_COLORS: Record<string, string> = {
  critical: 'bg-red-500/20 text-red-400',
  high: 'bg-orange-500/20 text-orange-400',
  medium: 'bg-amber-500/20 text-amber-400',
  low: 'bg-green-500/20 text-green-400',
};

export default function WorkflowsPage() {
  const { data: kanban, isLoading, refetch } = useWorkflowKanban();
  const createMutation = useCreateWorkflow();
  const transitionMutation = useTransitionWorkflow();
  const [showCreate, setShowCreate] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('medium');

  const handleCreate = async () => {
    if (!title) return;
    await createMutation.mutateAsync({ title, description, priority, entity_ids: [], tags: [] });
    setShowCreate(false);
    setTitle('');
    setDescription('');
    refetch();
  };

  const handleTransition = async (wf: Workflow, newStatus: string) => {
    await transitionMutation.mutateAsync({ workflow_id: wf.id, new_status: newStatus });
    refetch();
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-100">Investigations</h1>
          <p className="text-sm text-gray-500 mt-1">Workflow d'investigation structure — vue Kanban</p>
        </div>
        <button
          onClick={() => setShowCreate(!showCreate)}
          className="px-4 py-2 text-sm font-medium bg-cyber-cyan/10 text-cyber-cyan rounded-lg hover:bg-cyber-cyan/20 transition-colors"
        >
          + Nouvelle investigation
        </button>
      </div>

      {showCreate && (
        <div className="glass-panel p-5 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Titre de l'investigation..."
              className="px-3 py-2 text-sm bg-surface-tertiary border border-border-subtle rounded-lg text-gray-300 col-span-2"
            />
            <select
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
              className="px-3 py-2 text-sm bg-surface-tertiary border border-border-subtle rounded-lg text-gray-300"
            >
              <option value="low">Basse</option>
              <option value="medium">Moyenne</option>
              <option value="high">Haute</option>
              <option value="critical">Critique</option>
            </select>
          </div>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Description..."
            className="w-full px-3 py-2 text-sm bg-surface-tertiary border border-border-subtle rounded-lg text-gray-300 h-20 resize-none"
          />
          <div className="flex justify-end gap-2">
            <button onClick={() => setShowCreate(false)} className="px-4 py-2 text-sm text-gray-400">Annuler</button>
            <button
              onClick={handleCreate}
              disabled={!title || createMutation.isPending}
              className="px-4 py-2 text-sm font-medium bg-cyber-cyan text-surface-primary rounded-lg hover:bg-cyan-400 disabled:opacity-50"
            >
              Creer
            </button>
          </div>
        </div>
      )}

      {isLoading && (
        <div className="flex items-center justify-center py-16">
          <div className="w-8 h-8 border-2 border-cyber-cyan/30 border-t-cyber-cyan rounded-full animate-spin" />
        </div>
      )}

      {/* Kanban board */}
      {kanban && (
        <div className="flex gap-4 overflow-x-auto pb-4">
          {STATUS_ORDER.map((status) => {
            const workflows = kanban[status] || [];
            return (
              <div key={status} className={`flex-shrink-0 w-72 border-t-2 ${STATUS_COLORS[status] || 'border-gray-500'}`}>
                <div className="flex items-center justify-between px-3 py-2 bg-surface-tertiary/50 rounded-t-lg">
                  <span className="text-sm font-semibold text-gray-300">{STATUS_LABELS[status] || status}</span>
                  <span className="px-2 py-0.5 text-xs bg-surface-primary text-gray-400 rounded-full">{workflows.length}</span>
                </div>
                <div className="space-y-2 p-2 min-h-[200px] bg-surface-secondary/30 rounded-b-lg">
                  {workflows.map((wf: Workflow) => (
                    <div key={wf.id} className="glass-panel p-3 space-y-2">
                      <div className="flex items-start justify-between">
                        <h4 className="text-sm font-medium text-gray-200 leading-tight">{wf.title}</h4>
                        <span className={`px-1.5 py-0.5 text-xs rounded ${PRIORITY_COLORS[wf.priority] || ''}`}>
                          {wf.priority}
                        </span>
                      </div>
                      {wf.description && (
                        <p className="text-xs text-gray-500 line-clamp-2">{wf.description}</p>
                      )}
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-600">
                          {wf.steps?.filter((s) => s.completed).length || 0}/{wf.steps?.length || 0} etapes
                        </span>
                        {status !== 'closed' && (
                          <button
                            onClick={() => {
                              const idx = STATUS_ORDER.indexOf(status);
                              if (idx < STATUS_ORDER.length - 1) handleTransition(wf, STATUS_ORDER[idx + 1]);
                            }}
                            className="text-xs text-cyber-cyan hover:text-cyan-300"
                          >
                            Avancer →
                          </button>
                        )}
                      </div>
                      <div className="text-xs text-gray-600">
                        {new Date(wf.created_at).toLocaleDateString('fr-FR')}
                        {wf.assigned_to && ` — ${wf.assigned_to}`}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
